/*
 * tg-kids-weekly-progress
 *
 * Stage tabs for the Kids Creatine week-by-week section.
 *
 * Deliberate implementation notes:
 *
 * 1. Custom element rather than an IIFE. assets/tg-kids-claude-testimonials.js
 *    uses a bare IIFE that runs once at parse time, so its tabs go dead after a
 *    Shopify theme-editor section reload. connectedCallback re-runs on every
 *    re-insert, which is what a merchant editing settings actually needs.
 *
 * 2. This file contains NO chart code. Each stage panel renders its own chart,
 *    already truncated in Liquid to that stage's reveal point, so there is no
 *    chart state for the JS to hold and nothing that can desync from the tabs.
 *    The redraw animation is CSS keyed on :not([data-inactive]) becoming true,
 *    which happens as a side effect of activate() below.
 *
 * 3. Inactive panels are hidden with [data-inactive] + [inert], never
 *    display:none. An SVG inside a display:none ancestor has no resolved layout
 *    box, which makes it awkward to measure or export later.
 *
 * 4. All queries are scoped to `this`. There are intentionally no document- or
 *    window-level listeners: sections/ecom-faq.liquid already binds a GLOBAL
 *    document.querySelectorAll(".faq--item") listener that also drives the
 *    buy-box accordions, so this file stays out of the shared namespace.
 */
if (!customElements.get("tg-kids-weekly-progress")) {
  customElements.define(
    "tg-kids-weekly-progress",
    class TgKidsWeeklyProgress extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === "true") return;
        this.dataset.ready = "true";

        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

        if (!this.tabs.length || !this.panels.length) return;

        this.onTabClick = this.onTabClick.bind(this);
        this.onTabKeydown = this.onTabKeydown.bind(this);

        this.tabs.forEach((tab) => {
          tab.addEventListener("click", this.onTabClick);
          tab.addEventListener("keydown", this.onTabKeydown);
        });

        // Sync to whatever the server rendered as selected. focus:false is
        // essential here: focusing on load would steal focus from the top of
        // the page and scroll-jump the shopper down to this section.
        var initial =
          this.tabs.find(function (t) {
            return t.getAttribute("aria-selected") === "true";
          }) || this.tabs[0];

        this.activate(initial, { focus: false });
      }

      disconnectedCallback() {
        this.tabs.forEach((tab) => {
          tab.removeEventListener("click", this.onTabClick);
          tab.removeEventListener("keydown", this.onTabKeydown);
        });
      }

      onTabClick(event) {
        this.activate(event.currentTarget);
      }

      onTabKeydown(event) {
        var index = this.tabs.indexOf(event.currentTarget);
        if (index === -1) return;

        var next = null;
        var last = this.tabs.length - 1;

        if (event.key === "ArrowRight" || event.key === "ArrowDown") {
          next = index === last ? 0 : index + 1;
        } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
          next = index === 0 ? last : index - 1;
        } else if (event.key === "Home") {
          next = 0;
        } else if (event.key === "End") {
          next = last;
        }

        if (next === null) return;

        event.preventDefault();
        this.activate(this.tabs[next]);
      }

      activate(tab, options) {
        if (!tab) return;

        var targetId = tab.getAttribute("aria-controls");

        this.tabs.forEach(function (item) {
          var isActive = item === tab;
          item.classList.toggle("is-active", isActive);
          item.setAttribute("aria-selected", isActive ? "true" : "false");
          item.tabIndex = isActive ? 0 : -1;
        });

        this.panels.forEach(function (panel) {
          var isActive = panel.id === targetId;
          // Removing data-inactive is also what re-triggers the chart's CSS
          // build-in animation. See note 2 above.
          panel.toggleAttribute("data-inactive", !isActive);
          panel.toggleAttribute("inert", !isActive);
        });

        if (!options || options.focus !== false) {
          tab.focus();
        }
      }
    },
  );
}
